import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { apiService } from '../../services/api';
import { formatDate as formatDateTz } from '../../utils/timezoneUtils';

interface Request {
  id: number;
  request_number: string;
  status: string;
  progress_percentage: number;
  company_name: string;
  business_type: string;
  created_at: string;
  updated_at: string;
  payment_status: string;
  total_amount: number;
  paid_amount: number;
  package_name: string;
  package_price: number;
  package_currency: string;
  country_name: string;
  country_flag: string;
  template_info: {
    id: number | null;
    name: string | null;
    name_ar: string | null;
    is_dynamic: boolean;
  };
  field_preview: {
    key: string;
    value: string;
  }[];
}

const CreateTicket: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState<Request[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string>('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const getCountryFlagImage = (countryFlag: string, countryName: string) => {
    // countryFlag from database: country code or emoji
    const isEmoji = countryFlag && countryFlag.length > 2;
    const fallbackEmoji = isEmoji ? countryFlag : '🌍';
    const countryCode = isEmoji ? '' : countryFlag.toUpperCase();

    if (countryCode && countryCode.length <= 3) {
      return (
        <img
          src={`https://flagcdn.com/48x36/${countryCode.toLowerCase()}.png`}
          alt={`${countryName} flag`}
          className="w-5 h-3 object-cover rounded-sm inline"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback text-lg';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }

    return <span className="text-lg">{fallbackEmoji}</span>;
  };

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      const result = await apiService.getUserRequests();
      setRequests(result.requests || []);
    } catch (error) {
      setError('Error loading requests');    } finally {
      setRequestsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(selectedFile.type)) {
        alert('Only images (JPG, PNG, GIF), PDFs, and Word documents are allowed');
        e.target.value = '';
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRequestId || !message.trim()) {
      setError('Please select a request and enter a message');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      let requestBody;
      let requestHeaders: any = {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      };

      if (file) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('request_id', selectedRequestId);
        formData.append('message', message);
        formData.append('attachment', file);
        requestBody = formData;
      } else {
        // Use JSON for text-only tickets
        requestHeaders['Content-Type'] = 'application/json';
        requestBody = JSON.stringify({
          request_id: selectedRequestId,
          message: message
        });
      }

      const response = await fetch('https://patrixglobal.com/backend/api/tickets/create.php', {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody,
      });

      if (!response.ok) {
        throw new Error('Failed to create ticket');
      }

      const data = await response.json();
      if (data.message && data.ticket) {
        setSuccess(t('dashboard.tickets.ticketCreated'));
        
        // Redirect to ticket detail after a short delay
        setTimeout(() => {
          navigate(`/dashboard/tickets/${data.ticket.id}`);
        }, 2000);
      } else {
        setError(data.error || 'Failed to create ticket');
      }
    } catch (error) {
      setError('Error creating ticket');    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTz(dateString, locale);
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status.toLowerCase() as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Ticket Created Successfully!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {success}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            You will be redirected to the ticket detail page shortly...
          </p>
          <div className="mt-6">
            <Link
              to="/dashboard/tickets"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              View All Tickets
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4">
          <Link
            to="/dashboard/tickets"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Tickets
          </Link>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900">
          {t('dashboard.tickets.create')}
        </h1>
        <p className="mt-2 text-gray-600">
          Create a support ticket related to one of your requests
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Ticket Details
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Select Request */}
          <div className="mb-6">
            <label htmlFor="request" className="block text-sm font-medium text-gray-700 mb-2">
              {t('dashboard.tickets.selectRequest')} *
            </label>
            
            {requestsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">No requests found</p>
                <Link
                  to="/dashboard/create-request"
                  className="mt-2 inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
                >
                  Create your first request
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedRequestId === request.id.toString()
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedRequestId(request.id.toString())}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="request"
                        value={request.id}
                        checked={selectedRequestId === request.id.toString()}
                        onChange={(e) => setSelectedRequestId(e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {request.request_number} - {request.package_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {getCountryFlagImage(request.country_flag, request.country_name)} {request.country_name} • Created {formatDateDisplay(request.created_at)}
                            </p>
                          </div>
                          <div>
                            {getStatusBadge(request.status)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              {t('dashboard.tickets.message')} *
            </label>
            <textarea
              id="message"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe your issue or question in detail..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-2">
              {t('dashboard.tickets.attachFile')} (Optional)
            </label>
            <input
              id="file"
              type="file"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              accept="image/*,.pdf,.doc,.docx"
            />
            <p className="mt-1 text-xs text-gray-500">
              Max file size: 10MB. Allowed: Images, PDFs, Word documents
            </p>
            {file && (
              <p className="mt-1 text-sm text-green-600">
                Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Link
              to="/dashboard/tickets"
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </Link>
            
            <button
              type="submit"
              disabled={loading || !selectedRequestId || !message.trim() || requestsLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                t('dashboard.tickets.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTicket;